import is from '@sindresorhus/is';
import CryptoJS from 'crypto-js';
import { jwtDecode } from 'jwt-decode';
import type { IdTokenClaims } from 'oidc-client-ts';
import { ErrorResponse, User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { z } from 'zod';

import type { Clock } from '../clock/clock';
import type { OidcConfiguration, OidcProvider } from '../customization/customization-response';
import { HTTPError } from '../http-fetcher/http-error';
import type { AuthorizationTokenSource } from '../http-fetcher/http-fetcher';
import type { Logger } from '../logger/logger';
import type { ValidatingHttpFetcher } from '../validating-http-fetcher/validating-http-fetcher';
import type { AuthorizationProvider } from './authorization-provider';
import type { AuthorizationTokenResponsePayload, AuthorizationTokenTransformed } from './authorization-token';
import { authorizationTokenDecodedTransformedSchema } from './authorization-token';
import { sessionResponseSchema, sessionTransferResponseSchema } from './session';

const legacyRefreshTokenStorageKey = 'rt';
const legacyRefreshTokenSoftExpiryStorageKey = 'rte';
const providerIdStorageKey = 'pid';
const tokenRefreshLockName = 'cobrainer-token-refresh';
const minimumTokenValidityInMillis = 60_000;
const tokenRequestTimeoutInSeconds = 20;
const terminalTokenErrors = new Set(['invalid_client', 'invalid_grant', 'unauthorized_client']);
const reservedLoginParameters = new Set(['client_id', 'redirect_uri', 'response_type', 'scope', 'state']);
const urlSchema = z.url();

export const selfServicePoolUrl = 'https://cb-self-service-prod.auth.eu-central-1.amazoncognito.com';
export const selfServiceLogin = 'https://signin.cobrainer.com/signin';

export type LocationApi = Pick<Location, 'href' | 'origin' | 'search'> & {
    readonly assign: (url: string) => ReturnType<Location['assign']>;
    readonly replace: (url: string) => ReturnType<Location['replace']>;
};

export type RefreshLock = {
    readonly request: <T>(name: string, task: () => Promise<T>) => Promise<T>;
};

export type CognitoAuthProviderDependencies = {
    readonly apiBaseUrl: URL;
    readonly clock: Clock;
    readonly history?: Pick<History, 'replaceState'>;
    readonly location: LocationApi;
    readonly locks?: RefreshLock;
    readonly logger: Logger;
    readonly storage: Storage;
    readonly validatingHttpFetcher: ValidatingHttpFetcher;
};

export type CognitoAuthProviderOptions = {
    readonly oidcConfiguration: OidcConfiguration;
};

export type CognitoAuthorizationProvider = AuthorizationProvider & AuthorizationTokenSource;

type Session = {
    readonly attributes: AuthorizationTokenTransformed;
    readonly expiresAtInMillis: number;
    readonly jsonWebToken: string;
};

const isRejectedTokenExchange = (error: Error): boolean =>
    error instanceof HTTPError && error.response.status >= 400 && error.response.status < 500;

const isTerminalTokenError = (error: unknown): boolean =>
    error instanceof ErrorResponse && terminalTokenErrors.has(error.error ?? '');

const withoutAuthorizationResponse = (href: string): string => {
    const url = new URL(href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');

    return url.href;
};

const toSession = (user: User | null): Maybe<Session> => {
    if (is.null(user) || !is.nonEmptyString(user.id_token)) {
        return Maybe.nothing();
    }

    try {
        const claims = jwtDecode<AuthorizationTokenResponsePayload & { readonly exp: number }>(user.id_token);
        const attributes = authorizationTokenDecodedTransformedSchema.parse(claims);

        return Maybe.just({ attributes, expiresAtInMillis: claims.exp * 1000, jsonWebToken: user.id_token });
    } catch (_: unknown) {
        return Maybe.nothing();
    }
};

export const createCognitoAuthorizationProvider = async (
    dependencies: CognitoAuthProviderDependencies,
    options: CognitoAuthProviderOptions,
): Promise<CognitoAuthorizationProvider> => {
    const { clock, history, location, locks, logger, storage, validatingHttpFetcher } = dependencies;
    const { oidcConfiguration } = options;

    let oidcProvider: Maybe<OidcProvider> = Maybe.nothing();
    let userManager: Maybe<UserManager> = Maybe.nothing();
    let session: Maybe<Session> = Maybe.nothing();
    let pendingRefresh: Maybe<Promise<Maybe<Session>>> = Maybe.nothing();
    let isInitialized = false;

    const createUserManager = ({ loginUrl }: OidcProvider): Maybe<UserManager> => {
        const clientId = loginUrl.searchParams.get('client_id');

        if (!is.nonEmptyString(clientId)) {
            return Maybe.nothing();
        }

        const manager = new UserManager({
            authority: loginUrl.origin,
            // Renewal goes through getAuthorizationToken so it shares the in-tab and cross-tab refresh lock.
            automaticSilentRenew: false,
            client_id: clientId,
            extraQueryParams: Object.fromEntries(
                [...loginUrl.searchParams].filter(([key]) => !reservedLoginParameters.has(key)),
            ),
            loadUserInfo: false,
            metadata: {
                authorization_endpoint: new URL(loginUrl.pathname, loginUrl.origin).href,
                issuer: loginUrl.origin,
                token_endpoint: new URL('/oauth2/token', loginUrl.origin).href,
            },
            redirect_uri: location.origin,
            requestTimeoutInSeconds: tokenRequestTimeoutInSeconds,
            scope: loginUrl.searchParams.get('scope') ?? 'openid',
            stateStore: new WebStorageStateStore({ store: storage }),
            userStore: new WebStorageStateStore({ store: storage }),
        });

        manager.events.addAccessTokenExpiring(() => {
            void getAuthorizationToken();
        });

        return Maybe.just(manager);
    };

    const isFresh = ({ expiresAtInMillis }: Session): boolean =>
        expiresAtInMillis - clock.getCurrentTimestamp() > minimumTokenValidityInMillis;

    const endSession = async (): Promise<void> => {
        session = Maybe.nothing();
        storage.removeItem(providerIdStorageKey);

        if (userManager.isJust) {
            await userManager.value.removeUser();
        }
    };

    // Storage is cleared before the refresh lock is released so a waiting tab cannot reuse the
    // rejected refresh token. During startup the login page shows instead of an IdP logout redirect.
    const endRejectedSession = async (): Promise<Maybe<Session>> => {
        await endSession();

        if (isInitialized) {
            redirectToLogout();
        }

        return Maybe.nothing();
    };

    const withRefreshLock = <T>(task: () => Promise<T>): Promise<T> =>
        is.undefined(locks) ? task() : locks.request(tokenRefreshLockName, task);

    const refreshSession = (manager: UserManager, staleToken: string): Promise<Maybe<Session>> => {
        if (pendingRefresh.isJust) {
            return pendingRefresh.value;
        }

        const refresh = withRefreshLock(async (): Promise<Maybe<Session>> => {
            const storedUser = await manager.getUser();

            // Another tab may have logged out, or refreshed while this one waited for the lock.
            if (is.null(storedUser)) {
                return endRejectedSession();
            }

            const storedSession = toSession(storedUser);

            if (
                storedSession.isJust &&
                isFresh(storedSession.value) &&
                storedSession.value.jsonWebToken !== staleToken
            ) {
                return storedSession;
            }

            try {
                // signinSilent overrides the configured timeout with its own argument, so it is passed here too.
                return toSession(
                    await manager.signinSilent({ silentRequestTimeoutInSeconds: tokenRequestTimeoutInSeconds }),
                );
            } catch (error: unknown) {
                logger.error('[Cognito] Token refresh failed', error);

                // A rejected refresh token ends the session; network failures and transient OAuth errors
                // keep the last token so a request can still succeed and the next one retries the refresh.
                return isTerminalTokenError(error) ? endRejectedSession() : session;
            }
        })
            .then((refreshedSession) => {
                if (refreshedSession.isJust) {
                    session = refreshedSession;
                }

                return refreshedSession;
            })
            .finally(() => {
                pendingRefresh = Maybe.nothing();
            });

        pendingRefresh = Maybe.just(refresh);

        return refresh;
    };

    const getAuthorizationToken: AuthorizationTokenSource['getAuthorizationToken'] = async ({ rejectedToken } = {}) => {
        if (userManager.isNothing || session.isNothing) {
            return Maybe.nothing();
        }

        const { jsonWebToken } = session.value;

        // A rejected token that is no longer current was already replaced by another request's renewal.
        if (isFresh(session.value) && rejectedToken !== jsonWebToken) {
            return Maybe.just(jsonWebToken);
        }

        const refreshedSession = await refreshSession(userManager.value, rejectedToken ?? jsonWebToken);

        return refreshedSession.map(({ jsonWebToken }) => jsonWebToken);
    };

    const redirectToLogout = (): void => {
        if (oidcProvider.isNothing) {
            return;
        }

        const { loginUrl } = oidcProvider.value;

        if (loginUrl.toString().startsWith(selfServicePoolUrl)) {
            location.assign(selfServiceLogin);
        } else {
            const redirectUrl = new URL('/logout', loginUrl);
            redirectUrl.searchParams.set('client_id', loginUrl.searchParams.get('client_id') ?? '');
            redirectUrl.searchParams.set('logout_uri', location.origin);

            location.assign(redirectUrl.href);
        }
    };

    const logout = (): void => {
        if (session.isNothing) {
            return;
        }

        void endSession().finally(redirectToLogout);
    };

    // Tokens from outside the library's own flows (IdP-initiated codes, transferred or legacy refresh
    // tokens) are exchanged directly. The stored session is only replaced after a successful exchange.
    const establishSessionFromGrant = async (
        manager: UserManager,
        grant: { readonly code: string } | { readonly refresh_token: string },
    ): Promise<Result<unknown, Error>> => {
        const tokenResponse = await validatingHttpFetcher.post(sessionResponseSchema, {
            requestOptions: {
                body: new URLSearchParams({
                    client_id: manager.settings.client_id,
                    ...('code' in grant
                        ? { code: grant.code, grant_type: 'authorization_code', redirect_uri: location.origin }
                        : { grant_type: 'refresh_token', refresh_token: grant.refresh_token }),
                }).toString(),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                timeout: tokenRequestTimeoutInSeconds * 1000,
            },
            url: new URL('/oauth2/token', manager.settings.authority),
        });

        if (tokenResponse.isErr) {
            logger.error('[Cognito] Token exchange failed', tokenResponse.error);
            return tokenResponse;
        }

        const { expiresInSeconds, jsonWebToken, refreshJsonWebToken } = tokenResponse.value;
        await manager.storeUser(
            new User({
                access_token: jsonWebToken,
                expires_at: Math.floor(clock.getCurrentTimestamp() / 1000) + expiresInSeconds,
                id_token: jsonWebToken,
                profile: jwtDecode<IdTokenClaims>(jsonWebToken),
                refresh_token: refreshJsonWebToken ?? ('refresh_token' in grant ? grant.refresh_token : undefined),
                token_type: 'Bearer',
            }),
        );
        // Reading the user back loads it into the library, which arms its token-expiring timer.
        session = toSession(await manager.getUser());

        return Result.ok();
    };

    // A 401 with a fresh token means the session was revoked. A 401 with an expired token means the
    // renewal failed for a transient reason, so the refresh token is kept for the next attempt.
    const onUnauthorized = (): void => {
        if (session.isJust && !isFresh(session.value)) {
            return;
        }

        logout();
    };

    const establishSessionFromRedirect = async (manager: UserManager): Promise<Maybe<string>> => {
        try {
            const user = await manager.signinRedirectCallback(location.href);
            session = toSession(user);

            return is.string(user.state) ? Maybe.just(user.state) : Maybe.nothing();
        } catch (error: unknown) {
            logger.warn('[Cognito] Could not complete the sign-in redirect', error);

            return Maybe.nothing();
        }
    };

    const establishSessionFromSessionTransfer = async (
        manager: UserManager,
        nonce: string,
        sessionId: string,
    ): Promise<void> => {
        const transfer = await validatingHttpFetcher.get(sessionTransferResponseSchema, {
            requestOptions: { searchParams: { sessionId } },
            url: new URL('/users/me/session-transfer', dependencies.apiBaseUrl),
        });

        if (transfer.isErr) {
            logger.error('[Cognito] Session transfer failed', transfer.error);
            return;
        }

        const refreshToken = CryptoJS.AES.decrypt(transfer.value.encrypted, nonce).toString(CryptoJS.enc.Utf8);
        await establishSessionFromGrant(manager, { refresh_token: refreshToken });
    };

    const restoreSession = async (manager: UserManager): Promise<void> => {
        session = toSession(await manager.getUser());

        if (session.isJust) {
            await getAuthorizationToken();
            return;
        }

        await migrateLegacyRefreshToken(manager);
    };

    // The legacy keys stay until the exchange succeeds or the token is rejected, so a migration that
    // failed for a transient reason is retried on the next start.
    const migrateLegacyRefreshToken = async (manager: UserManager): Promise<void> => {
        const legacyRefreshToken = storage.getItem(legacyRefreshTokenStorageKey);
        const legacySoftExpiry = storage.getItem(legacyRefreshTokenSoftExpiryStorageKey);
        const legacyTokenIsUsable =
            is.nonEmptyString(legacyRefreshToken) &&
            !(is.nonEmptyString(legacySoftExpiry) && Date.parse(legacySoftExpiry) < clock.getCurrentTimestamp());

        if (legacyTokenIsUsable) {
            const migration = await establishSessionFromGrant(manager, { refresh_token: legacyRefreshToken });

            if (migration.isErr && !isRejectedTokenExchange(migration.error)) {
                return;
            }
        }

        storage.removeItem(legacyRefreshTokenStorageKey);
        storage.removeItem(legacyRefreshTokenSoftExpiryStorageKey);
    };

    const findProviderById = (providerId: number): Maybe<OidcProvider> =>
        Maybe.of(oidcConfiguration.providers[providerId] ?? oidcConfiguration.providers[0]);

    const determineOidcProvider = (idpInitiated: boolean, selfServiceInitiated: boolean): Maybe<OidcProvider> => {
        const { providers } = oidcConfiguration;

        if (idpInitiated) {
            return Maybe.of(providers.find(({ loginUrl }) => loginUrl.searchParams.has('idp_initiated_allowed')));
        }

        if (selfServiceInitiated) {
            return Maybe.of(providers.find(({ loginUrl }) => loginUrl.href.startsWith(selfServicePoolUrl)));
        }

        return findProviderById(Number.parseInt(storage.getItem(providerIdStorageKey) ?? '0', 10));
    };

    const selectOidcProvider = (provider: Maybe<OidcProvider>): void => {
        if (provider.isJust) {
            storage.setItem(providerIdStorageKey, oidcConfiguration.providers.indexOf(provider.value).toString());
        } else {
            storage.removeItem(providerIdStorageKey);
        }

        oidcProvider = provider;
        userManager = provider.andThen(createUserManager);
    };

    const redirectToCallbackUri = (callbackUri: Maybe<string>): void => {
        if (callbackUri.isJust && urlSchema.safeParse(callbackUri.value).success) {
            history?.replaceState({}, document.title, callbackUri.value);
        }
    };

    const establishSessionFromUrl = async (manager: UserManager, query: URLSearchParams): Promise<void> => {
        const code = query.get('code');
        const state = query.get('state');
        const nonce = query.get('nonce');
        const sessionId = query.get('sessionId');

        if (is.string(code) && state === 'IDP_INITIATED') {
            // IdP-initiated logins arrive without a PKCE verifier or stored state, so the library cannot
            // process their code.
            await establishSessionFromGrant(manager, { code });
            redirectToCallbackUri(Maybe.just(withoutAuthorizationResponse(location.href)));
        } else if (is.string(code) && is.string(state)) {
            redirectToCallbackUri(await establishSessionFromRedirect(manager));
        } else if (is.string(nonce) && is.string(sessionId)) {
            await establishSessionFromSessionTransfer(manager, nonce, sessionId);
            redirectToCallbackUri(Maybe.of(storage.getItem('callback_uri')));
            storage.removeItem('callback_uri');
        }
    };

    const initialize = async (): Promise<void> => {
        const query = new URLSearchParams(location.search);
        const idpInitiated = query.get('state') === 'IDP_INITIATED';
        const selfServiceInitiated = query.has('nonce') && query.has('sessionId');

        selectOidcProvider(determineOidcProvider(idpInitiated, selfServiceInitiated));

        if (userManager.isNothing) {
            return;
        }

        const manager = userManager.value;
        await establishSessionFromUrl(manager, query);

        // A failed login from the URL, such as a reloaded IdP-initiated URL with an already used code,
        // falls back to the stored session.
        if (session.isNothing) {
            await restoreSession(manager);
        }

        await manager.clearStaleState();
    };

    await initialize();
    isInitialized = true;

    return {
        getAuthorizationToken,
        get hideLogoutButtons() {
            return oidcProvider.mapOr(false, ({ hideLogoutButtons }) => hideLogoutButtons);
        },
        get isBlocked() {
            return session.mapOr(false, ({ attributes }) => is.emptyArray(attributes.roles) || attributes.isService);
        },
        get isLoggedIn() {
            return session.isJust;
        },
        login(oidcProviderId: number, callbackUri?: string) {
            selectOidcProvider(findProviderById(oidcProviderId));

            if (oidcProvider.isNothing || userManager.isNothing) {
                return;
            }

            if (oidcProvider.value.loginUrl.href.startsWith(selfServicePoolUrl)) {
                location.replace(selfServiceLogin);
            } else {
                void userManager.value.signinRedirect({
                    redirectMethod: 'replace',
                    state: callbackUri ?? location.href,
                });
            }
        },
        logout,
        onUnauthorized,
        get roles() {
            return session.mapOr([], ({ attributes }) => attributes.roles);
        },
    };
};
